/**
 * Real on-chain settlement — SOL + USDC (SPL) transfers to the SFN treasury.
 * ---------------------------------------------------------------------------
 * Browser-only. All RPC traffic is routed through the same-origin proxy
 * (`/api/public/solana-rpc`) so no public endpoint can 403 the browser origin
 * and no provider key ever reaches the client.
 *
 * Flow:
 *   1. build transfer instruction(s) (native SOL or USDC ATA→ATA)
 *   2. request signature from the injected wallet
 *      (signAndSendTransaction, falling back to signTransaction + send)
 *   3. poll getSignatureStatuses until confirmed / finalized
 *
 * `@solana/web3.js` + `@solana/spl-token` are imported dynamically so the
 * SSR bundle and the initial page payload stay untouched.
 */

import { sha256 } from "@noble/hashes/sha256";

import { RPC_PROXY_PATH } from "./solana-rpc";

export const TOKEN_PROGRAM_ID = "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA";
export const ASSOCIATED_TOKEN_PROGRAM_ID = "ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL";
export const SYSTEM_PROGRAM_ID = "11111111111111111111111111111111";

/** Mainnet USDC mint (Circle). */
export const USDC_MINT = "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v";
export const USDC_DECIMALS = 6;
export const LAMPORTS_PER_SOL = 1_000_000_000;

export type PayCurrency = "SOL" | "USDC";

export type PaymentPhase =
  | "idle"
  | "building"
  | "awaiting-approval"
  | "confirming"
  | "success"
  | "error";

export type PaymentProgress = (phase: PaymentPhase, signature?: string) => void;

export class PaymentError extends Error {
  code: "no-wallet" | "rejected" | "insufficient" | "network" | "timeout" | "unknown";
  constructor(code: PaymentError["code"], message: string) {
    super(message);
    this.name = "PaymentError";
    this.code = code;
  }
}

type InjectedWallet = {
  publicKey?: { toString: () => string } | null;
  signAndSendTransaction?: (tx: unknown) => Promise<{ signature: string }>;
  signTransaction?: (tx: unknown) => Promise<{ serialize: () => Uint8Array }>;
};

/**
 * `@solana/web3.js` / `@solana/spl-token` expect Node's Buffer global.
 * Installed on demand so the polyfill never ships with the initial bundle.
 */
export async function ensureBufferGlobal(): Promise<void> {
  const g = globalThis as unknown as Record<string, unknown>;
  if (!g["Buffer"]) {
    const mod = await import("buffer");
    g["Buffer"] = mod.Buffer;
  }
  if (!g["process"]) g["process"] = { env: {} };
}

/** Absolute proxy URL — `Connection`/fetch need an origin-qualified endpoint. */
export function rpcEndpoint(): string {
  if (typeof window === "undefined") return RPC_PROXY_PATH;
  return `${window.location.origin}${RPC_PROXY_PATH}`;
}

async function rpc<T>(method: string, params: unknown[]): Promise<T> {
  const res = await fetch(rpcEndpoint(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ jsonrpc: "2.0", id: 1, method, params }),
  });
  const json = (await res.json().catch(() => ({}))) as {
    result?: T;
    error?: { message?: string };
  };
  if (!res.ok || json.error) {
    throw new PaymentError("network", json.error?.message ?? `RPC HTTP ${res.status}`);
  }
  return json.result as T;
}

/** Maps a wallet/RPC error to a user-facing PaymentError. */
export function classifyWalletError(err: unknown): PaymentError {
  if (err instanceof PaymentError) return err;
  const msg = String((err as { message?: string } | undefined)?.message ?? err ?? "");
  const low = msg.toLowerCase();
  if (low.includes("user rejected") || low.includes("declined") || low.includes("cancel")) {
    return new PaymentError("rejected", "Transaction rejected in wallet.");
  }
  if (low.includes("insufficient") || low.includes("0x1 ") || low.includes("debit an account")) {
    return new PaymentError("insufficient", "Insufficient balance for this transfer.");
  }
  if (low.includes("blockhash") || low.includes("timeout") || low.includes("expired")) {
    return new PaymentError("timeout", "Network confirmation timed out. Please retry.");
  }
  if (low.includes("rpc") || low.includes("fetch") || low.includes("network")) {
    return new PaymentError("network", "Solana network unreachable. Please retry.");
  }
  return new PaymentError("unknown", msg || "Transaction failed.");
}

/** Amount → smallest unit, guarding against float dust. */
export function toBaseUnits(amount: number, decimals: number): bigint {
  if (!isFinite(amount) || amount <= 0) throw new PaymentError("unknown", "Invalid amount.");
  return BigInt(Math.round(amount * 10 ** decimals));
}

/**
 * Polls `getSignatureStatuses` until the transaction reaches `confirmed`
 * (or `finalized`). Resolves with the confirmation level, throws on chain
 * error or timeout.
 */
export async function awaitConfirmation(
  signature: string,
  { timeoutMs = 60_000, intervalMs = 1_500 } = {},
): Promise<"confirmed" | "finalized"> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const res = await rpc<{
      value: Array<{ confirmationStatus?: string; err?: unknown } | null>;
    }>("getSignatureStatuses", [[signature], { searchTransactionHistory: true }]).catch(
      () => null,
    );
    const status = res?.value?.[0];
    if (status) {
      if (status.err) throw new PaymentError("unknown", "Transaction failed on-chain.");
      if (status.confirmationStatus === "confirmed" || status.confirmationStatus === "finalized") {
        return status.confirmationStatus;
      }
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  throw new PaymentError("timeout", "Confirmation timed out. Check the signature on Solscan.");
}

/** Associated USDC token account for an owner (used by the transfer path). */
export async function usdcAtaFor(owner: string): Promise<string> {
  await ensureBufferGlobal();
  const { PublicKey } = await import("@solana/web3.js");
  return deriveAta(PublicKey, new PublicKey(owner)).toBase58();
}

type PublicKeyCtor = typeof import("@solana/web3.js")["PublicKey"];
type Pk = InstanceType<PublicKeyCtor>;

/**
 * Associated-token-account PDA, derived with web3.js only. Deriving it here
 * (instead of via @solana/spl-token) keeps a single PublicKey implementation
 * in play, which avoids cross-module derivation failures in bundled/SSR
 * environments.
 */
function deriveAta(PublicKey: PublicKeyCtor, owner: Pk): Pk {
  return findProgramAddress(
    PublicKey,
    [
      new Uint8Array(owner.toBuffer()),
      new Uint8Array(new PublicKey(TOKEN_PROGRAM_ID).toBuffer()),
      new Uint8Array(new PublicKey(USDC_MINT).toBuffer()),
    ],
    new PublicKey(ASSOCIATED_TOKEN_PROGRAM_ID),
  );
}

const PDA_MARKER = new TextEncoder().encode("ProgramDerivedAddress");

function concatBytes(chunks: Uint8Array[]): Uint8Array {
  const total = chunks.reduce((n, c) => n + c.length, 0);
  const out = new Uint8Array(total);
  let o = 0;
  for (const c of chunks) {
    out.set(c, o);
    o += c.length;
  }
  return out;
}

/**
 * Program-derived address, computed on plain `Uint8Array`s.
 * web3.js' own helper concatenates Node `Buffer`s, which fail the strict
 * `Uint8Array` checks of its hashing dependency in non-Node realms.
 */
export function findProgramAddress(
  PublicKey: PublicKeyCtor,
  seeds: Uint8Array[],
  programId: Pk,
): Pk {
  const programBytes = new Uint8Array(programId.toBuffer());
  for (let nonce = 255; nonce > 0; nonce--) {
    const hash = sha256(
      concatBytes([...seeds, Uint8Array.from([nonce]), programBytes, PDA_MARKER]),
    );
    if (!PublicKey.isOnCurve(hash)) return new PublicKey(hash);
  }
  throw new PaymentError("unknown", "Unable to derive token account address.");
}

/** SPL `TransferChecked` (instruction 12) data buffer. */
export function transferCheckedData(amount: bigint, decimals: number): Uint8Array {
  const data = new Uint8Array(10);
  data[0] = 12;
  new DataView(data.buffer).setBigUint64(1, amount, true);
  data[9] = decimals;
  return data;
}

export type SendPaymentArgs = {
  wallet: InjectedWallet;
  payer: string;
  destination: string;
  currency: PayCurrency;
  amount: number;
  onProgress?: PaymentProgress;
};

/**
 * Builds, signs, sends and confirms a real transfer to the treasury.
 * Returns the confirmed transaction signature.
 */
export async function sendTreasuryPayment({
  wallet,
  payer,
  destination,
  currency,
  amount,
  onProgress,
}: SendPaymentArgs): Promise<string> {
  if (!wallet || !payer) throw new PaymentError("no-wallet", "Connect a Solana wallet first.");
  onProgress?.("building");

  await ensureBufferGlobal();
  const web3 = await import("@solana/web3.js");
  const { PublicKey, Transaction, SystemProgram } = web3;
  const payerKey = new PublicKey(payer);
  const destKey = new PublicKey(destination);

  const tx = new Transaction();

  if (currency === "SOL") {
    tx.add(
      SystemProgram.transfer({
        fromPubkey: payerKey,
        toPubkey: destKey,
        lamports: Number(toBaseUnits(amount, 9)),
      }),
    );
  } else {
    const { TransactionInstruction } = web3;
    const mint = new PublicKey(USDC_MINT);
    const fromAta = deriveAta(PublicKey, payerKey);
    const toAta = deriveAta(PublicKey, destKey);
    const tokenProgram = new PublicKey(TOKEN_PROGRAM_ID);

    // Create the treasury's USDC account on first-ever USDC settlement.
    const toInfo = await rpc<{ value: unknown }>("getAccountInfo", [
      toAta.toBase58(),
      { encoding: "base64", commitment: "confirmed" },
    ]).catch(() => ({ value: null }));
    if (!toInfo?.value) {
      tx.add(
        new TransactionInstruction({
          programId: new PublicKey(ASSOCIATED_TOKEN_PROGRAM_ID),
          keys: [
            { pubkey: payerKey, isSigner: true, isWritable: true },
            { pubkey: toAta, isSigner: false, isWritable: true },
            { pubkey: destKey, isSigner: false, isWritable: false },
            { pubkey: mint, isSigner: false, isWritable: false },
            { pubkey: new PublicKey(SYSTEM_PROGRAM_ID), isSigner: false, isWritable: false },
            { pubkey: tokenProgram, isSigner: false, isWritable: false },
          ],
          data: Buffer.from([1]), // idempotent create
        }),
      );
    }

    tx.add(
      new TransactionInstruction({
        programId: tokenProgram,
        keys: [
          { pubkey: fromAta, isSigner: false, isWritable: true },
          { pubkey: mint, isSigner: false, isWritable: false },
          { pubkey: toAta, isSigner: false, isWritable: true },
          { pubkey: payerKey, isSigner: true, isWritable: false },
        ],
        data: Buffer.from(transferCheckedData(toBaseUnits(amount, USDC_DECIMALS), USDC_DECIMALS)),
      }),
    );
  }

  const { value: blockhashInfo } = await rpc<{ value: { blockhash: string } }>(
    "getLatestBlockhash",
    [{ commitment: "confirmed" }],
  );
  tx.recentBlockhash = blockhashInfo.blockhash;
  tx.feePayer = payerKey;

  onProgress?.("awaiting-approval");

  let signature: string;
  try {
    if (typeof wallet.signAndSendTransaction === "function") {
      const res = await wallet.signAndSendTransaction(tx);
      signature = res.signature;
    } else if (typeof wallet.signTransaction === "function") {
      const signed = await wallet.signTransaction(tx);
      const raw = signed.serialize();
      const b64 =
        typeof btoa === "function"
          ? btoa(String.fromCharCode(...new Uint8Array(raw)))
          : Buffer.from(raw).toString("base64");
      signature = await rpc<string>("sendRawTransaction", [
        b64,
        { encoding: "base64", skipPreflight: false, preflightCommitment: "confirmed" },
      ]);
    } else {
      throw new PaymentError("no-wallet", "This wallet cannot sign Solana transactions.");
    }
  } catch (err) {
    throw classifyWalletError(err);
  }

  onProgress?.("confirming", signature);
  await awaitConfirmation(signature);
  onProgress?.("success", signature);
  return signature;
}
